import React, { useState, useEffect } from "react";
import { Tooltip, Popover, Modal, message } from "antd";
import { AntdButton } from "../../../blueplanitUtils/InputField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faSquareCheck,
  faSquareXmark,
  faUserSlash,
  faCalendarAlt,
  faCalendarXmark,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { setLoader } from "../../../Slices/loaderSlice";
import {
  InputField,
  DatePickerField,
} from "../../../blueplanitUtils/InputField";
import dayjs from "dayjs";
import { fetchAdminAttendanceAsync } from "../../../Slices/attendenceSlice";
import { updateLoginWorkHistoryThunkByAdmin } from "../../../Slices/timerslice";

const AttendenceAdmin = () => {
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [editedSessions, setEditedSessions] = useState({});
  const [form, setForm] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  });
  const [appliedRange, setAppliedRange] = useState({
    from: moment().startOf("month").toDate(),
    to: moment().endOf("month").toDate(),
  });
  const [editingCell, setEditingCell] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const loginWorkhistory = useSelector(
    (state) => state.Attendence.adminAttendance
  );
  const loginUser = useSelector(
    (state) => state.loginUserInfo?.loginUserDetails
  );

  const handleEdit = (loginHistoryId, dayKey, date, userId) => {
    setEditingCell({ loginHistoryId, dayKey, date, userId });
    setIsEditModalVisible(true);
  };

  const handleDateChangeInCell = (newDate) => {
    if (!newDate || !editingCell) return;

    setEditedSessions((prev) => ({
      ...prev,
      [editingCell.userId]: {
        ...(prev[editingCell.userId] || {}),
        [editingCell.dayKey]: newDate,
      },
    }));


  };

  useEffect(() => {
    if (loginUser?.id) {
      dispatch(fetchAdminAttendanceAsync({ empId: loginUser.id }));
    }
  }, [dispatch, loginUser?.id]);

  const getDaysBetweenDates = (from, to) => {
    const days = [];
    const start = moment(from).clone();
    const end = moment(to).clone();
    let index = 1;

    while (start.isSameOrBefore(end, "day")) {
      days.push({
        title: start.format("DD-MMM"),
        dataIndex: `day${index}`,
        key: `day${index}`,
        fullDate: start.clone(),
      });
      start.add(1, "day");
      index++;
    }

    return days;
  };
  const handleSubmitEditedSession = (userId, dayKey) => {
    const editedDate = editedSessions[userId]?.[dayKey];
    if (!editedDate) return;

    Modal.confirm({
      title: "Confirm Logout Time Update",
      content:
        "Please check once again. Logout time cannot be updated again after submission.",
      okText: "Submit",
      cancelText: "Cancel",
      okButtonProps: {
        style: {
          backgroundColor: "#28a745",
          borderColor: "#28a745",
          color: "#fff",
        },
      },
      onOk: async () => {
        try {
          await dispatch(
            updateLoginWorkHistoryThunkByAdmin({
              id: editingCell.loginHistoryId,
              endTime: editedDate.toISOString(),
            })
          );

          // ✅ Update state to reflect the new date in UI
          setEditedSessions((prev) => {
            const newSessions = { ...prev };
            delete newSessions[userId];
            return newSessions;
          });

          setEditingCell(null);

          // ✅ Success feedback
          message.success("Logout time updated successfully.");

          // ✅ Optionally re-fetch the data for consistency
          handleSearch();
        } catch (error) {
          console.error("Failed to update login work history:", error);
          message.error("Failed to update logout time. Please try again.");
        }
      },
    });
  };

  const selectedStart = moment(appliedRange?.from);
  const selectedMonth = selectedStart.month() + 1;
  const selectedYear = selectedStart.year();

  const renderStatusImage = (status) => {
    const commonStyle = {
      width: "13px",
      height: "13px",
      objectFit: "contain",
      display: "block",
      margin: "0 auto",
    };

    switch (status) {
      case "present":
        return (
          <FontAwesomeIcon
            icon={faSquareCheck}
            style={{ ...commonStyle, color: "rgba(8, 146, 47, 1)" }}
            title="Present"
          />
        );
      case "absent":
        return (
          <FontAwesomeIcon
            icon={faSquareXmark}
            style={{ ...commonStyle, color: "rgba(122, 118, 118, 1)" }}
            title="Absent"
          />
        );
      case "leave":
        return (
          <FontAwesomeIcon
            icon={faUserSlash}
            style={{ ...commonStyle, color: "orange" }}
            title="Leave"
          />
        );
      case "holiday":
        return (
          <FontAwesomeIcon
            icon={faCalendarXmark}
            style={{ ...commonStyle, color: "#ed7474ff" }}
            title="Holiday"
          />
        );
      case "future":
        return (
          <FontAwesomeIcon
            icon={faCalendarAlt}
            style={{ ...commonStyle, color: "rgba(3, 167, 217, 0.96)" }}
            title="Future"
          />
        );
      default:
        return null;
    }
  };

  const dayColumns = getDaysBetweenDates(appliedRange.from, appliedRange.to);
  const columns = [
    {
      title: "Employee Name",
      dataIndex: "employeeName",
      key: "employeeName",
      align: "center",
      fixed: "left",
      width: 150
    },
    ...dayColumns.map((col) => ({
      ...col,
      align: "center",
    })),
  ];

  const groupedByDepartment = {};

  loginWorkhistory?.forEach((employee) => {
    const fullName = `${employee.firstname} ${employee.lastname}`.toLowerCase();
    if (!fullName.includes(searchText.toLowerCase())) return;

    // Employee Access filtering is handled by the backend via getEmployeeAccessScope
    // No frontend department filter needed — backend returns only allowed employees

    const department = employee.department || "Other";

    const from = moment(appliedRange.from);
    const to = moment(appliedRange.to);
    const totalDays = to.diff(from, "days") + 1;

    const workHistoryData = {
      ...employee,
      employeeName: `${employee.firstname} ${employee.lastname}`,
      userId: employee._id,
    };

    for (let day = 0; day < totalDays; day++) {
      const currentDay = moment(from).add(day, "days");
      const dayKey = `day${day + 1}`;

      if (appliedRange.from && currentDay.isBefore(moment(appliedRange.from), "day"))
        continue;
      if (appliedRange.to && currentDay.isAfter(moment(appliedRange.to), "day"))
        continue;

      const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
      if (isWeekend) {
        workHistoryData[dayKey] = renderStatusImage("holiday");
        continue;
      }

      const isHoliday = employee.holidays?.some((h) =>
        moment(h.holidayDate).isSame(currentDay, "day")
      );
      if (isHoliday) {
        workHistoryData[dayKey] = renderStatusImage("holiday");
        continue;
      }

      if (currentDay.isAfter(moment(), "day")) {
        const isLeave = employee.approvedLeaves?.some((leave) => {
          const start = moment(+leave.startDate);
          const end = moment(+leave.endDate);
          return (
            currentDay.isBetween(start, end, "day", "[]") &&
            leave.leaveStatus === "Approved"
          );
        });
        workHistoryData[dayKey] = isLeave
          ? renderStatusImage("leave")
          : renderStatusImage("future");
        continue;
      }

      const isLeave = employee.approvedLeaves?.some((leave) => {
        const start = moment(+leave.startDate);
        const end = moment(+leave.endDate);
        if (
          currentDay.isBetween(start, end, "day", "[]") &&
          leave.leaveStatus === "Approved"
        ) {
          workHistoryData[dayKey] = renderStatusImage("leave");
          return true;
        }
        return false;
      });
      if (isLeave) continue;

      const hasPunchInOut = employee?.attendanceRecords?.some((record) => {
        const inTime = moment(+record.punchIn);
        const outTime = moment(+record.punchOut);
        return inTime.isSame(currentDay, "day") && outTime.isSame(currentDay, "day");
      });

      const loginSessionsToday = employee.loginWorkHistories?.filter((session) => {
        const start = moment(session.startTime);
        const end = session.endTime ? moment(session.endTime) : null;
        return (
          start.isSame(currentDay, "day") ||
          (end && end.isSame(currentDay, "day")) ||
          (start.isBefore(currentDay.endOf("day")) &&
            (!end || end.isAfter(currentDay.startOf("day"))))
        );
      });

      if (loginSessionsToday && loginSessionsToday.length > 0) {
        const firstSession = loginSessionsToday[0];
        const hasMultipleSessions = loginSessionsToday.length > 1;

        const renderAllSessions = () => (
          <div style={{ maxHeight: "200px", overflowY: "auto" }}>
            {loginSessionsToday.map((session, index) => {
              const loginTime = moment(session.startTime).format("HH:mm");
              const logoutTime = session.endTime
                ? moment(session.endTime).format("HH:mm")
                : "Active";

              return (
                <div
                  key={index}
                  style={{
                    borderBottom:
                      index < loginSessionsToday.length - 1
                        ? "1px dashed #ddd"
                        : "none",
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {/* Existing UI layout */}
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span>Login Time: {loginTime}</span>
                      <span>Logout Time: {logoutTime}</span>
                    </div>

                    {/* Show Edit button only if session is Active (no endTime) */}
                    {!session.endTime && (
                      <AntdButton
                        style={{
                          fontSize: 7,
                          cursor: "pointer",
                          backgroundColor: "#007bff",
                          border: "none",
                          borderRadius: 3,
                          height: 20,
                          color: "white",
                          padding: "0px 4px",
                          marginLeft: 4,
                          whiteSpace: "nowrap",
                        }}
                        label="Edit"
                        onClick={() =>
                          handleEdit(
                            session._id,
                            dayKey,
                            currentDay.toDate(),
                            employee._id
                          )
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );

        workHistoryData[dayKey] = (
          <div style={{ textAlign: "center", fontSize: 10, lineHeight: 1.2 }}>
            {renderStatusImage("present")}

            <div style={{ marginTop: 4 }}>
              <Popover
                placement="right"
                trigger="hover"
                content={renderAllSessions()}
                title="All Sessions"
              >
                <span
                  style={{
                    color: "#1890ff",
                    cursor: "pointer",
                    fontSize: 9,
                    textDecoration: "underline",
                    whiteSpace: "nowrap",
                  }}
                >
                  View
                </span>
              </Popover>
            </div>
          </div>
        );

      }
    }

    if (!groupedByDepartment[department]) {
      groupedByDepartment[department] = [];
    }

    groupedByDepartment[department].push(workHistoryData);
  });

  const handleRefresh = async () => {
    const currentMonthStart = moment().startOf("month").toDate();
    const currentMonthEnd = moment().endOf("month").toDate();

    setForm({
      from: currentMonthStart,
      to: currentMonthEnd,
    });

    setAppliedRange({
      from: currentMonthStart,
      to: currentMonthEnd,
    });

    dispatch(setLoader(true));
    try {
      await dispatch(
        fetchAdminAttendanceAsync({
          fromDate: moment(currentMonthStart).toISOString(),
          toDate: moment(currentMonthEnd).toISOString(),
        })
      );
    } catch (error) {
      console.error("Error during refresh:", error);
    } finally {
      dispatch(setLoader(false));
    }
  };

  const handleSearch = async () => {
    if (!form.from || !form.to) return;

    dispatch(setLoader(true));
    try {
      const fromDate = moment(form.from).toISOString();
      const toDate = moment(form.to).toISOString();

      setAppliedRange({
        from: form.from,
        to: form.to,
      });
      const empId = loginUser?.id;
      await dispatch(fetchAdminAttendanceAsync({ empId, fromDate, toDate }));
    } catch (error) {
      console.error("Error during search:", error);
    } finally {
      dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateChange = (date, field) => {
    if (field === "from") {
      setForm({
        from: date ? date.startOf("day").toDate() : null,
        to: null,
      });
    } else {
      setForm((prev) => ({
        ...prev,
        [field]: date ? date.startOf("day").toDate() : null,
      }));
    }
  };

  const EditModal = () => {
    if (!editingCell) return null;

    return (
      <Modal
        title="Edit Logout Time"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingCell(null);
        }}
        footer={[
          <AntdButton
            key="cancel"
            onClick={() => {
              setIsEditModalVisible(false);
              setEditingCell(null);
            }}
            label="Cancel"
          />,
          <AntdButton
            key="submit"
            type="primary"
            onClick={() =>
              handleSubmitEditedSession(editingCell.userId, editingCell.dayKey)
            }
            label="Update"
            style={{ backgroundColor: "#28a745", borderColor: "#28a745" }}
          />,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <label>Select New Logout Time:</label>
          <DatePickerField
            classname="form-control"
            showTime={{ format: "HH:mm" }}
            format="YYYY-MM-DD HH:mm"
            value={
              editedSessions[editingCell.userId]?.[editingCell.dayKey]
                ? dayjs(editedSessions[editingCell.userId][editingCell.dayKey])
                : dayjs(editingCell.date)
            }
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            onChange={(date) => handleDateChangeInCell(date?.toDate())}
          />
        </div>
      </Modal>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row">
          <div className="col-md-2 mb-2">
            <InputField
              type="text"
              className="input-search"
              placeholder="Search By Name"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
          <div className="col-md-3 mb-2">
            <div style={{ display: "flex", alignItems: "center" }}>
              <label style={{ marginRight: "8px", marginTop: "-14px" }}>
                From Date:
              </label>
              <DatePickerField
                classname="form-control"
                name="from"
                placeholder="From Date"
                value={form?.from ? dayjs(form.from) : null}
                format="DD/MM/YYYY"
                onChange={(date) => handleDateChange(date, "from")}
                disabledDate={(current) => {
                  return (
                    current &&
                    (current > dayjs(form.to).startOf("day") ||
                      current > dayjs().endOf("day"))
                  );
                }}
              />
            </div>
          </div>
          <div className="col-md-3 mb-2">
            <div style={{ display: "flex", alignItems: "center" }}>
              <label style={{ marginRight: "8px", marginTop: "-14px" }}>
                To Date:
              </label>
              <DatePickerField
                classname="form-control"
                name="to"
                placeholder="To Date"
                value={form?.to ? dayjs(form.to) : null}
                format="DD/MM/YYYY"
                onChange={(date) => handleDateChange(date, "to")}
                disabledDate={(current) => {
                  return current && current < dayjs(form.from).startOf("day");
                }}
              />
            </div>
          </div>
          <div className="col-md-2 mb-2">
            <AntdButton
              label="Search"
              style={{
                backgroundColor: "#007bff",
                color: "white",
                marginLeft: "-30px",
              }}
              onClick={() => handleSearch()}
              disabled={!form.from || !form.to}
            />
          </div>
          <div className="col-md-2 mb-2">
            <Tooltip title="Sync Now">
              <FontAwesomeIcon
                icon={faSync}
                style={{
                  fontSize: "24px",
                  color: "#1890ff",
                  cursor: "pointer",
                  marginLeft: "-100px",
                  marginRight: "50px",
                  marginTop: "5px",
                }}
                onClick={handleRefresh}
              />
            </Tooltip>
          </div>
        </div>

        {Object.entries(groupedByDepartment).map(([department, employees]) => (
          <div key={department} className="mb-4">
            <h5>{department}</h5>
            <CustomGridTable
              columns={columns}
              Id={"attendence-admin-table-"+department}
              dataSource={employees}
              rowKey="userId"
              pagination={false}
              scroll={{ x: "max-content"}}
            />
          </div>
        ))}
      </div>
      <EditModal />
    </div>
  );
};

export default AttendenceAdmin;
